/**
 * Authentication configuration constants
 * Shared between frontend and backend
 *
 * @module auth/constants
 */

import commonPasswordsList from './common-passwords.json';

/**
 * Authentication configuration based on PRD requirements
 */
export const AUTH_CONFIG = {
  /**
   * Session duration in days (FR88)
   * 90 days with "Remember Me" checked
   */
  SESSION_DURATION_DAYS: 90,

  /**
   * Password policy requirements (FR111)
   * Story 23.12: min 12 → 8, max capped at 64 (coordinated with the Firebase
   * server-side policy on staging + prod). 8-char floor per NIST 800-63B;
   * 64-char cap per OWASP ASVS ("allow ≥ 64"). Retained U/L/N + common-password
   * blocklist (FR113) keep the strength posture defensible at the lower floor.
   */
  PASSWORD_MIN_LENGTH: 8,
  PASSWORD_MAX_LENGTH: 64,
  PASSWORD_REQUIRES_UPPERCASE: true,
  PASSWORD_REQUIRES_LOWERCASE: true,
  PASSWORD_REQUIRES_NUMBER: true,
  PASSWORD_REQUIRES_SPECIAL: false, // Story 15.7: Optional for Apple/Google password manager compatibility

  /**
   * Magic link expiration (FR140)
   * Links expire after 1 hour
   */
  MAGIC_LINK_EXPIRATION_MINUTES: 60,

  /**
   * Password reset link expiration (FR140)
   * Links expire after 1 hour
   */
  PASSWORD_RESET_EXPIRATION_MINUTES: 60,

  /**
   * Account lockout settings (FR115)
   * Firebase Auth handles this automatically
   */
  MAX_LOGIN_ATTEMPTS: 5,
} as const;

/**
 * Authentication route paths
 * Used for navigation and redirects
 */
export const AUTH_ROUTES = {
  LOGIN: '/auth/login',
  SIGNUP: '/auth/signup',
  RESET_PASSWORD: '/auth/reset-password',
  RESET_PASSWORD_CONFIRM: '/auth/reset-password/confirm',
  MAGIC_LINK_REQUEST: '/auth/magic-link',
  MAGIC_LINK_COMPLETE: '/auth/complete',
  CHILD_PROFILES: '/auth/child-profiles',
  ONBOARDING: '/onboarding',
  HOME: '/',
} as const;

/**
 * Local storage keys for authentication
 * Used to store temporary auth-related data
 */
export const AUTH_STORAGE_KEYS = {
  /**
   * Email used for magic link sign-in
   * Stored in localStorage during magic link flow
   */
  EMAIL_FOR_SIGNIN: 'emailForSignIn',

  /**
   * URL to redirect to after authentication
   * Used to restore user's intended destination
   */
  REDIRECT_URL: 'authRedirectUrl',

  /**
   * Timestamp when user started onboarding
   * Used for analytics (FR26 - 5 minute target)
   */
  ONBOARDING_START_TIME: 'onboardingStartTime',
} as const;

/**
 * Password strength levels for UI feedback (FR112)
 */
export enum PasswordStrength {
  WEAK = 'weak',
  MODERATE = 'moderate',
  STRONG = 'strong',
  VERY_STRONG = 'very_strong',
}

/**
 * Common passwords to blacklist (FR113)
 * Top 300+ most common passwords from security research
 * Loaded from common-passwords.json for maintainability
 */
export const COMMON_PASSWORDS = commonPasswordsList;

/**
 * Set of common passwords for O(1) lookup performance
 * Used by isCommonPassword() in password-validation.ts
 */
export const COMMON_PASSWORDS_SET = new Set(
  COMMON_PASSWORDS.map((pwd: string) => pwd.toLowerCase())
);

/**
 * Email action code settings for Firebase Auth
 * Used for magic link and password reset
 */
export const getActionCodeSettings = (continueUrl: string) => ({
  url: continueUrl,
  handleCodeInApp: true,
  iOS: {
    bundleId: 'fi.sagamoon.app', // Future mobile app
  },
  android: {
    packageName: 'fi.sagamoon.app', // Future mobile app
    installApp: false,
  },
  dynamicLinkDomain: 'sagamoon.page.link', // Configure in Firebase Console
});
